import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { createInquiry } from "@/lib/api";
import { CheckCircle2, ArrowRight, Mail, Phone, MapPin } from "lucide-react";

export default function Contact() {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", subject: "", message: "", source: "contact", consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error(t("toast.fill_required"));
      return;
    }
    if (!form.consent) { toast.error(t("contact.consent_required")); return; }
    setSubmitting(true);
    try {
      await createInquiry(form);
      setDone(true);
      toast.success(t("toast.contact_ok"));
    } catch (err) {
      // FastAPI 422 returns an array of pydantic error objects; show a friendly message.
      const raw = err?.response?.data?.detail;
      const friendly = Array.isArray(raw)
        ? t("contact.error_validation")
        : (typeof raw === "string" ? raw : t("toast.generic_error"));
      toast.error(friendly);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact" className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-24">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="tr-eyebrow">{t("contact.eyebrow")}</div>
          <h1 className="tr-section-title text-5xl lg:text-7xl mt-4">{t("contact.title_a")}<br />{t("contact.title_b")}</h1>
          <p className="mt-6 text-[var(--tr-muted)] text-lg leading-relaxed max-w-md">{t("contact.body")}</p>

          <div className="mt-10 space-y-6">
            <div className="flex items-start gap-4">
              <span className="w-10 h-10 rounded-full bg-[var(--tr-sage)] flex items-center justify-center">
                <Mail className="w-4 h-4 text-[var(--tr-deep-green)]" />
              </span>
              <div>
                <div className="text-xs uppercase tracking-[0.24em] text-[var(--tr-muted)] font-bold">{t("contact.email_label")}</div>
                <a href="mailto:truerootsagro@gmail.com" className="mt-1 block font-display font-bold text-lg text-[var(--tr-deep-green)]">truerootsagro@gmail.com<br/><span className="text-sm text-[var(--tr-muted)] font-normal">info@truerootsagro.in</span></a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <span className="w-10 h-10 rounded-full bg-[var(--tr-sage)] flex items-center justify-center">
                <Phone className="w-4 h-4 text-[var(--tr-deep-green)]" />
              </span>
              <div>
                <div className="text-xs uppercase tracking-[0.24em] text-[var(--tr-muted)] font-bold">{t("contact.phone_label")}</div>
                <a href="tel:+919326721459" className="mt-1 block font-display font-bold text-lg text-[var(--tr-deep-green)]">+91 93267 21459</a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <span className="w-10 h-10 rounded-full bg-[var(--tr-sage)] flex items-center justify-center">
                <MapPin className="w-4 h-4 text-[var(--tr-deep-green)]" />
              </span>
              <div>
                <div className="text-xs uppercase tracking-[0.24em] text-[var(--tr-muted)] font-bold">{t("contact.office_label")}</div>
                <div className="mt-1 font-display font-bold text-lg text-[var(--tr-deep-green)] leading-snug">TrueRoots Agro Foods LLP<br />{t("footer.office_line2")}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          {done ? (
            <div data-testid="contact-success" className="tr-card p-10 text-center">
              <CheckCircle2 className="w-14 h-14 text-[var(--tr-lime)] mx-auto" />
              <h3 className="font-display font-extrabold text-3xl mt-4 text-[var(--tr-deep-green)]">{t("contact.success_title")}</h3>
              <p className="mt-3 text-[var(--tr-muted)]">{t("contact.success_body")}</p>
            </div>
          ) : (
            <form data-testid="contact-form" onSubmit={submit} className="tr-card p-8 lg:p-10 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <label className="block">
                  <span className="tr-label">{t("contact.f_name")}*</span>
                  <input required data-testid="contact-name" className="tr-input" value={form.name} onChange={onChange("name")} />
                </label>
                <label className="block">
                  <span className="tr-label">{t("contact.f_email")}*</span>
                  <input required type="email" data-testid="contact-email" className="tr-input" value={form.email} onChange={onChange("email")} />
                </label>
                <label className="block">
                  <span className="tr-label">{t("contact.f_phone")}</span>
                  <input inputMode="tel" data-testid="contact-phone" className="tr-input" value={form.phone} onChange={onChange("phone")} />
                </label>
                <label className="block">
                  <span className="tr-label">{t("contact.f_i_am")}</span>
                  <select data-testid="contact-source" className="tr-input" value={form.source} onChange={onChange("source")}>
                    <option value="contact">{t("contact.role_visitor")}</option>
                    <option value="farmer">{t("contact.role_farmer")}</option>
                    <option value="fpo">{t("contact.role_fpo")}</option>
                    <option value="buyer">{t("contact.role_buyer")}</option>
                    <option value="government">{t("contact.role_government")}</option>
                    <option value="research">{t("contact.role_research")}</option>
                    <option value="media">{t("contact.role_media")}</option>
                    <option value="career">{t("contact.role_career")}</option>
                    <option value="other">{t("contact.role_other")}</option>
                  </select>
                </label>
                <label className="block md:col-span-2">
                  <span className="tr-label">{t("contact.f_subject")}*</span>
                  <input required data-testid="contact-subject" className="tr-input" value={form.subject} onChange={onChange("subject")} />
                </label>
                <label className="block md:col-span-2">
                  <span className="tr-label">{t("contact.f_message")}*</span>
                  <textarea required data-testid="contact-message" className="tr-input tr-textarea" value={form.message} onChange={onChange("message")} />
                </label>
              </div>
              {/* DPDP Act consent — Round 3 Part B §3. Unticked by default, required. */}
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="contact-consent"
                  checked={form.consent}
                  onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))}
                  className="mt-1 w-5 h-5 accent-[var(--tr-deep-green)] flex-shrink-0"
                />
                <span className="text-sm text-[var(--tr-deep-green)] leading-relaxed">{t("contact.f_consent")}</span>
              </label>
              <button type="submit" data-testid="contact-submit" disabled={submitting} className="tr-pill-btn tr-btn-primary disabled:opacity-60">
                {submitting ? t("common.sending") : t("contact.f_submit")} <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
